import React from 'react'
import { Badge, BadgeColor, Icon } from '@pandiafi/design-system'
import { t } from 'i18next'

import { LegalLinks, resolveContacts } from './utils'
import { TContact } from '../../../common/validations/contractContacts'

import { RoundLink } from 'components'

interface AuthenticatedFooterProps {
  legalLinks: LegalLinks
  contacts?: TContact[]
}

export const AuthenticatedFooter: React.FC<AuthenticatedFooterProps> = ({
  legalLinks,
  contacts,
}) => {
  const { propertyManager, propertyMaintenance, customerService } =
    resolveContacts(contacts)

  return (
    <div className="w-full tablet:max-w-2xl phone:px-4 desktop:px-24 desktop:max-w-7xl grid mx-auto grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-y-10 md:gap-x-16 py-12 text-sm md:text-lg text-white">
      <div className="flex justify-center md:justify-start mb-10 col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4">
        <div className="flex items-center">
          <img
            src="/images/footer-logo.svg"
            alt="logo"
            className="mr-4 w-full max-w-[180px] max-h-14"
          />
        </div>
      </div>

      {propertyManager && (
        <div className="grid grid-cols-2 min-tablet:block">
          <div>
            <p className="mb-3 text-base min-tablet:text-xl font-medium">
              {propertyManager.officeName}
            </p>
            <p>{propertyManager.name}</p>
            {propertyManager.phoneNumber && (
              <p className="text-sm lg:text-base">
                <a href={`tel:${propertyManager.phoneNumber}`}>
                  {propertyManager.phoneNumber}
                </a>
              </p>
            )}
            <p className="text-sm lg:text-base">
              <a href={`mailto:${propertyManager.email}`}>
                {propertyManager.email}
              </a>
            </p>
            <Badge className="mt-2" color={BadgeColor.WHITE}>
              {t('contact:propertyManager')}
            </Badge>
          </div>
          <div className="flex justify-end min-tablet:hidden">
            {propertyManager.phoneNumber && (
              <RoundLink
                className="flex items-center mr-3"
                href={`tel:${propertyManager.phoneNumber}`}
              >
                <Icon icon="phone" size="lg" className="w-6" />
              </RoundLink>
            )}
            <RoundLink
              className="flex items-center"
              href={`mailto:${propertyManager.email}`}
            >
              <Icon icon="email" size="lg" className="w-6" />
            </RoundLink>
          </div>
        </div>
      )}
      {propertyMaintenance && (
        <div className="grid grid-cols-2 min-tablet:block">
          <div>
            <p className="mb-3 text-base min-tablet:text-xl font-medium">
              {propertyMaintenance.officeName}
            </p>
            <p>{propertyMaintenance.name}</p>
            {propertyMaintenance.phoneNumber && (
              <p className="text-sm lg:text-base">
                <a href={`tel:${propertyMaintenance.phoneNumber}`}>
                  {propertyMaintenance.phoneNumber}
                </a>
              </p>
            )}
            <p className="text-sm lg:text-base">
              <a href={`mailto:${propertyMaintenance.email}`}>
                {propertyMaintenance.email}
              </a>
            </p>
            <Badge className="mt-2" color={BadgeColor.WHITE}>
              {t('contact:propertyMaintenance')}
            </Badge>
          </div>
          <div className="flex justify-end min-tablet:hidden">
            {propertyMaintenance.phoneNumber && (
              <RoundLink
                className="flex items-center mr-3"
                href={`tel:${propertyMaintenance.phoneNumber}`}
              >
                <Icon icon="phone" size="lg" className="w-6" />
              </RoundLink>
            )}
            <RoundLink
              className="flex items-center"
              href={`mailto:${propertyMaintenance.email}`}
            >
              <Icon icon="email" size="lg" className="w-6" />
            </RoundLink>
          </div>
        </div>
      )}
      {customerService && (
        <div className="grid grid-cols-2 min-tablet:block">
          <div>
            <p className="mb-3 text-base min-tablet:text-xl font-medium">
              {t('contact:customerService')}
            </p>
            {customerService.phoneNumber && (
              <p className="text-sm lg:text-base">
                <a href={`tel:${customerService.phoneNumber}`}>
                  {customerService.phoneNumber}
                </a>
              </p>
            )}
            <p className="text-sm lg:text-base">
              <a href={`mailto:${customerService.email}`}>
                {customerService.email}
              </a>
            </p>
          </div>
          <div className="flex justify-end min-tablet:hidden">
            {customerService.phoneNumber && (
              <RoundLink
                className="flex items-center mr-3"
                href={`tel:${customerService.phoneNumber}`}
              >
                <Icon icon="phone" size="lg" className="w-6" />
              </RoundLink>
            )}
            <RoundLink
              className="flex items-center"
              href={`mailto:${customerService.email}`}
            >
              <Icon icon="email" size="lg" className="w-6" />
            </RoundLink>
          </div>
        </div>
      )}
      <div>
        <div className="space-y-6 underline text-common-blue-3 text-base">
          <div>
            {legalLinks.privacyStatement && (
              <p>
                <a
                  target="_blank"
                  className="underline"
                  href={legalLinks.privacyStatement}
                  rel="noreferrer"
                >
                  {t('contact:legal.privacyStatementLinkText')}
                </a>
              </p>
            )}
            {legalLinks.accessibilityStatement && (
              <p>
                <a
                  target="_blank"
                  className="underline"
                  href={legalLinks.accessibilityStatement}
                  rel="noreferrer"
                >
                  {t('contact:legal.accessibilityStatementLinkText')}
                </a>
              </p>
            )}
            {legalLinks.termsOfUse && (
              <p>
                <a
                  target="_blank"
                  className="underline"
                  href={legalLinks.termsOfUse}
                  rel="noreferrer"
                >
                  {t('contact:legal.termsOfUseLinkText')}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
